import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BellRinging,
  CheckCircle,
  Checks,
  DeviceMobile,
  List,
  Microphone,
  ShieldCheck,
  Sparkle,
  Wallet,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, formatErr } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

const MARQUEE_ITEMS = ["Preschools", "Montessori", "Coaching Centers", "Daycare", "Activity Clubs", "Primary Schools", "Tuition Classes", "Play Schools"];

export default function Landing() {
  const [form, setForm] = useState({ school_name: "", owner_name: "", email: "", phone: "", school_type: "", student_count: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, student_count: Number(form.student_count) || 0 });
      setSent(true);
      toast.success("Thanks! Our team will reach out within 24 hours.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] text-[#1A1A24]">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-[15px] flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-logo">
            <img src="/edujac-mark.png" alt="EDUJAC" className="h-[42px] w-[42px] object-contain" />
            <span className="font-heading font-extrabold text-2xl tracking-wide text-[#00194E]">EDUJAC</span>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <a href="#pricing" data-testid="nav-pricing-link" className="text-sm font-medium text-slate-600 hover:text-brand-coral transition-colors duration-200">
              Pricing
            </a>
            <Link to="/login" data-testid="nav-login-btn">
              <Button variant="ghost" className="rounded-full font-semibold">Log in</Button>
            </Link>
            <a href="#lead-form" data-testid="nav-register-btn">
              <Button className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-semibold transition-transform duration-300 hover:-translate-y-0.5 active:scale-95">
                Register Your School
              </Button>
            </a>
          </div>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen((o) => !o)}
            className="md:hidden p-2 rounded-xl text-[#00194E] hover:bg-slate-100 transition-colors duration-200"
            aria-label="Toggle navigation menu"
          >
            {menuOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl px-6 py-3 flex flex-col gap-1.5" data-testid="mobile-menu">
            <a href="#pricing" data-testid="mobile-nav-pricing" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-medium text-slate-600">
              Pricing
            </a>
            <Link to="/login" data-testid="mobile-nav-login" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-medium text-slate-600">
              Log in
            </Link>
            <a href="#lead-form" data-testid="mobile-nav-register" onClick={() => setMenuOpen(false)} className="pt-1.5 pb-2">
              <Button className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-semibold">Register Your School</Button>
            </a>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24 md:pt-24 grid md:grid-cols-2 gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 bg-brand-mint/15 text-teal-700 text-xs font-bold px-4 py-1.5 rounded-full mb-6">
            <ShieldCheck size={14} weight="duotone" /> Built for preschools, coaching centers & small schools
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]" data-testid="hero-heading">
            The school day,{" "}
            <span className="text-brand-coral">beautifully</span> organized.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-lg">
            One-tap attendance, AI-written homework, instant parent alerts and a feather-light fee ledger — everything your school needs, fast enough for any phone.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#lead-form" data-testid="hero-register-btn">
              <Button size="lg" className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 hover:-translate-y-0.5 active:scale-95">
                Request a Demo <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
            <a href="#features" data-testid="hero-features-btn">
              <Button size="lg" variant="outline" className="rounded-full font-bold px-8 border-slate-300 transition-transform duration-300 hover:-translate-y-0.5 active:scale-95">
                See features
              </Button>
            </a>
          </div>
        </motion.div>

        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <div className="absolute -top-6 -left-6 w-40 h-40 bg-brand-yellow/40 rounded-3xl -rotate-6" />
          <img
            src="https://images.pexels.com/photos/8422148/pexels-photo-8422148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Preschool children playing"
            className="relative rounded-3xl w-full object-cover aspect-[4/3] shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
            data-testid="hero-image"
          />
          <img
            src="https://images.pexels.com/photos/8422170/pexels-photo-8422170.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Kids at an activity table"
            className="absolute -bottom-10 -left-4 sm:-left-10 w-40 sm:w-52 rounded-2xl rotate-[-4deg] border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] object-cover aspect-square"
          />
          <div className="absolute -bottom-5 right-4 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.10)] px-5 py-3 flex items-center gap-3 border border-slate-100">
            <div className="w-9 h-9 rounded-full bg-status-green/15 grid place-items-center">
              <Checks size={20} weight="duotone" className="text-status-green" />
            </div>
            <div>
              <p className="text-sm font-bold leading-none">Attendance done</p>
              <p className="text-xs text-slate-500 mt-1">in 30 seconds flat</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Marquee */}
      <div className="border-y border-slate-100 bg-[#F4F5F7] py-4 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="mx-6 text-sm font-heading font-semibold text-slate-400 flex items-center gap-6">
              {item} <span className="text-brand-yellow">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <motion.div {...fadeUp}>
          <p className="text-sm font-bold text-brand-coral uppercase tracking-widest">Everything in one place</p>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-3 max-w-xl">
            Four tools your staff will actually love using
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <motion.div {...fadeUp} className="md:col-span-2 bg-white border border-slate-200 rounded-2xl p-8 relative overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300">
            <img
              src="https://images.pexels.com/photos/5306457/pexels-photo-5306457.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Teacher with students"
              className="w-full h-44 object-cover rounded-xl mb-6 md:mb-0 md:w-1/2 md:h-full md:absolute md:right-0 md:top-0 md:rounded-none md:opacity-90 md:[mask-image:linear-gradient(to_left,black,transparent)]"
            />
            <div className="relative z-10 max-w-sm">
              <CheckCircle size={36} weight="duotone" className="text-status-green" />
              <h3 className="font-heading text-xl font-bold mt-4">One-Tap Attendance</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                Everyone starts present. Teachers tap once to mark an absence — parents get an instant alert. A whole class in under a minute.
              </p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="bg-gradient-to-br from-brand-yellow/25 via-white to-brand-coral/15 border border-slate-200 rounded-2xl p-8 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300">
            <Sparkle size={36} weight="duotone" className="text-brand-coral" />
            <h3 className="font-heading text-xl font-bold mt-4">AI Homework Assistant</h3>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">
              "Suggest with AI" writes friendly, age-appropriate homework in seconds. Teachers can also just dictate — voice typing is built in.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white border border-brand-yellow/60 rounded-full px-4 py-2 text-xs font-bold text-slate-700">
              <Microphone size={14} weight="duotone" className="text-brand-coral" /> Voice-to-text included
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="bg-white border border-slate-200 rounded-2xl p-8 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300">
            <BellRinging size={36} weight="duotone" className="text-status-blue" />
            <h3 className="font-heading text-xl font-bold mt-4">Instant Broadcasts</h3>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">
              Holidays, events, fee reminders, birthday wishes — reach every parent instantly from the Broadcast Hub.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-2 bg-white border border-slate-200 rounded-2xl p-8 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300">
            <div className="flex flex-col sm:flex-row gap-8 items-start">
              <div className="max-w-sm">
                <Wallet size={36} weight="duotone" className="text-brand-coral" />
                <h3 className="font-heading text-xl font-bold mt-4">Feather-light Fee Ledger</h3>
                <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Paid vs. due at a glance with clear green/red coding. Record cash, bank or UPI references — or let parents pay online by card.
                </p>
              </div>
              <div className="flex-1 w-full space-y-2">
                <div className="flex items-center justify-between bg-status-green/10 rounded-xl px-4 py-2.5 text-sm">
                  <span className="font-semibold">Aarav P. — June Fee</span>
                  <span className="text-status-green font-bold">PAID ₹2,500</span>
                </div>
                <div className="flex items-center justify-between bg-status-red/10 rounded-xl px-4 py-2.5 text-sm">
                  <span className="font-semibold">Myra R. — June Fee</span>
                  <span className="text-status-red font-bold">DUE ₹2,500</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Parent app */}
      <section className="bg-[#1A1A24] text-white py-24">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-bold text-brand-yellow uppercase tracking-widest">For parents</p>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-3">A zero-type app parents open with a smile</h2>
            <ul className="mt-8 space-y-5">
              {[
                "Attendance alerts the moment a child is marked absent",
                "Daily homework & activity feed, written for humans",
                "Announcements and holiday notices in one stream",
                "Fee status with one-tap online payment",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-300">
                  <CheckCircle size={22} weight="duotone" className="text-brand-mint shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-slate-400">
              <DeviceMobile size={18} weight="duotone" className="text-brand-yellow" />
              Nothing to install — works beautifully on any phone browser
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="flex justify-center">
            <div className="w-72 bg-[#FDFDFD] text-[#1A1A24] rounded-[2rem] p-4 shadow-2xl rotate-2 border-4 border-slate-700">
              <div className="bg-brand-coral/10 rounded-2xl p-4">
                <p className="text-xs font-bold text-brand-coralDark">TODAY</p>
                <p className="font-heading font-bold mt-1">Diya is present ✦</p>
              </div>
              <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4">
                <p className="text-xs font-bold text-slate-400">HOMEWORK · MATHS</p>
                <p className="font-semibold text-sm mt-1">Counting 1–20</p>
                <p className="text-xs text-slate-500 mt-1">Practice counting spoons or blocks at home…</p>
              </div>
              <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4">
                <p className="text-xs font-bold text-slate-400">ANNOUNCEMENT</p>
                <p className="font-semibold text-sm mt-1">Summer Break: Jun 15–30</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <motion.div {...fadeUp} className="text-left">
          <p className="text-sm font-bold text-brand-coral uppercase tracking-widest">Pricing</p>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-3">Simple plans that grow with you</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { name: "Sprout", price: "₹999", sub: "per month", features: ["Up to 50 students", "Attendance + homework", "Parent app access", "Email support"], hot: false },
            { name: "Bloom", price: "₹1,999", sub: "per month", features: ["Up to 200 students", "Everything in Sprout", "AI homework assistant", "Online fee payments", "Priority support"], hot: true },
            { name: "Evergreen", price: "Custom", sub: "for school chains", features: ["Unlimited students", "Multiple branches", "Dedicated onboarding", "Custom integrations"], hot: false },
          ].map((plan) => (
            <motion.div
              key={plan.name}
              {...fadeUp}
              data-testid={`pricing-${plan.name.toLowerCase()}`}
              className={`rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                plan.hot
                  ? "bg-[#1A1A24] text-white border-transparent shadow-[0_20px_50px_rgb(255,107,107,0.25)] relative"
                  : "bg-white border-slate-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
              }`}
            >
              {plan.hot && (
                <span className="absolute -top-3 left-8 bg-brand-yellow text-[#1A1A24] text-xs font-extrabold px-3 py-1 rounded-full">MOST POPULAR</span>
              )}
              <h3 className="font-heading text-lg font-bold">{plan.name}</h3>
              <p className="mt-4">
                <span className="font-heading text-4xl font-extrabold">{plan.price}</span>{" "}
                <span className={`text-sm ${plan.hot ? "text-slate-400" : "text-slate-500"}`}>{plan.sub}</span>
              </p>
              <ul className="mt-6 space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className={`flex items-center gap-2.5 text-sm ${plan.hot ? "text-slate-300" : "text-slate-600"}`}>
                    <CheckCircle size={18} weight="duotone" className={plan.hot ? "text-brand-yellow" : "text-brand-mint"} /> {f}
                  </li>
                ))}
              </ul>
              <a href="#lead-form" className="block mt-8">
                <Button
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  className={`w-full rounded-full font-bold transition-transform duration-300 active:scale-95 ${
                    plan.hot ? "bg-brand-coral hover:bg-brand-coralDark text-white" : "bg-slate-100 hover:bg-brand-coral/10 hover:text-brand-coralDark text-slate-700"
                  }`}
                >
                  Get started
                </Button>
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Lead form */}
      <section id="lead-form" className="bg-[#F4F5F7] py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold">Register your school</h2>
            <p className="text-slate-600 mt-3">Tell us about your school and we'll set up a personalized demo within 24 hours.</p>
          </motion.div>
          <motion.form
            {...fadeUp}
            onSubmit={submitLead}
            data-testid="lead-form"
            className="mt-10 bg-white border border-slate-200 rounded-2xl p-8 grid sm:grid-cols-2 gap-5"
          >
            {sent ? (
              <div className="sm:col-span-2 text-center py-8" data-testid="lead-success">
                <CheckCircle size={48} weight="duotone" className="text-status-green mx-auto" />
                <h3 className="font-heading text-xl font-bold mt-4">You're on the list!</h3>
                <p className="text-slate-600 mt-2">Our team will contact you shortly to schedule your demo.</p>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="school_name">School name</Label>
                  <Input id="school_name" data-testid="lead-school-name" required className="mt-1.5" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} placeholder="Your School Name" />
                </div>
                <div>
                  <Label htmlFor="owner_name">Your name</Label>
                  <Input id="owner_name" data-testid="lead-owner-name" required className="mt-1.5" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Ananya Rao" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="lead-email" type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@school.in" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" data-testid="lead-phone" required className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98XXX XXXXX" />
                </div>
                <div>
                  <Label>School type</Label>
                  <Select value={form.school_type} onValueChange={(v) => setForm({ ...form, school_type: v })} required>
                    <SelectTrigger data-testid="lead-school-type" className="mt-1.5">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preschool">Preschool / Daycare</SelectItem>
                      <SelectItem value="coaching">Coaching center</SelectItem>
                      <SelectItem value="school">Primary / Secondary school</SelectItem>
                      <SelectItem value="activity">Activity club</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="student_count">Number of students</Label>
                  <Input id="student_count" data-testid="lead-student-count" type="number" min="1" required className="mt-1.5" value={form.student_count} onChange={(e) => setForm({ ...form, student_count: e.target.value })} placeholder="60" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message">Anything we should know? (optional)</Label>
                  <Textarea id="message" data-testid="lead-message" className="mt-1.5" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="We run two branches and need fee tracking…" />
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    data-testid="lead-form-submit-button"
                    disabled={sending}
                    className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold py-6 text-base transition-transform duration-300 active:scale-95"
                  >
                    {sending ? "Submitting…" : "Request my demo"}
                  </Button>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/edujac-mark.png" alt="EDUJAC" className="h-8 w-8 object-contain" />
            <span>
              <span className="block font-heading font-extrabold tracking-wide text-[#00194E] leading-none">EDUJAC</span>
              <span className="block text-[11px] font-semibold text-[#119687] mt-1">School ERP & Management Platform</span>
            </span>
          </div>
          <p className="text-sm text-slate-500">Made with care for little schools with big hearts.</p>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">EDUJAC Technologies LLP</p>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="footer-legal-nav">
            <Link to="/privacy" data-testid="footer-link-privacy" className="text-xs font-medium text-slate-500 hover:text-[#00194E] transition-colors duration-200">Privacy Policy</Link>
            <Link to="/data-protection" data-testid="footer-link-data-protection" className="text-xs font-medium text-slate-500 hover:text-[#00194E] transition-colors duration-200">Data Protection</Link>
            <Link to="/terms" data-testid="footer-link-terms" className="text-xs font-medium text-slate-500 hover:text-[#00194E] transition-colors duration-200">Terms of Service</Link>
            <Link to="/cookies" data-testid="footer-link-cookies" className="text-xs font-medium text-slate-500 hover:text-[#00194E] transition-colors duration-200">Cookie Policy</Link>
            <Link to="/data-retention" data-testid="footer-link-data-retention" className="text-xs font-medium text-slate-500 hover:text-[#00194E] transition-colors duration-200">Data Retention</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
